import { Divider } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent } from "vue";
import {
    createFieldDate,
    createFieldInput,
    createFieldSelect,
    createFieldTemplateBuilder,
    createFieldTextArea
} from "../../../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { conditionFormChecker, putField } from "../../../../../utils/form";
import { createField as createFieldInformInfo } from "../../../inform/request/inform-info/inform-info.section";

const Address = defineAsyncComponent(() => import("../../../../../components/address/Address"));
const HcProductList = defineAsyncComponent(() => import("./HcProductList"));
const DeclarationInlineList = defineAsyncComponent(() => import("../../../../../components/declaration/DeclarationInlineList"));

export const callInform = ({ searchQuery, userCompanyId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "refRequestDoc.key", type: "String", operator: "in", value: ["16", "17"] },
        { name: "products.refHaz.refSubStanceTypeMain.key", type: "String", operator: "=", value: "micro" },
        { name: "refUserCompany.id", type: "UUID", operator: "=", value: userCompanyId },
        { name: "products.refHc.id", type: "UUID", operator: "=", value: null }
    ];
    if (searchQuery) {
        where.push({ name: "approveCode", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/inform/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "approveCode", "refInspection.refSequestrate.key"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "approveCode", type: "DESC" }],
                children: [
                    {
                        name: "products",
                        select: ["id"],
                        page: 0,
                        size: 1,
                        where: [
                            { name: "refHazSpecies.key", type: "String", operator: "!=", value: "1" },
                            { name: "refHc.id", type: "UUID", operator: "=", value: null }
                        ]
                    }
                ]
            }
        })
        .then(page => page.data.filter(d => d.products.length > 0));
};

export const callInformFull = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/inform/search", {
            criteria: {
                select: ["**", "refUserCompany.refPersonType.*", "refUser.refPersonType.*"],
                page: 0,
                size: 1,
                where: [{ name: "id", type: "UUID", operator: "=", value: id }],
                children: [
                    {
                        name: "requests",
                        asName: "lastRequestApprove",
                        select: ["*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                        sort: [{ name: "createDate", type: "DESC" }],
                        children: [
                            {
                                name: "statuses",
                                asName: "lastStatusApprove",
                                select: ["**"],
                                page: 0,
                                size: 1,
                                where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                                sort: [{ name: "createDate", type: "DESC" }]
                            }
                        ]
                    },
                    {
                        name: "attachments",
                        select: ["*"],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "createDate", type: "ASC" }]
                    },
                    {
                        name: "products",
                        select: [
                            "**",
                            "refHazObject.refSpecies.*",
                            "refCoordinateCustoms.refUnit.*",
                            "refCoordinateCustoms.refDetailSubStanceType.*",
                            "refHaz.refManufactureCountry.*",
                            "refHaz.refSubStanceTypeMain.*",
                            "refHazLicense.refQuantityUnit.*"
                        ],
                        page: 0,
                        size: 1000000,
                        where: [
                            { name: "refHazSpecies.key", type: "String", operator: "!=", value: "1" },
                            { name: "refHaz.refSubStanceTypeMain.key", type: "String", operator: "=", value: "micro" },
                            { name: "refHc.id", type: "UUID", operator: "=", value: null }
                        ],
                        sort: [{ name: "createDate", type: "ASC" }],
                        children: [
                            {
                                name: "productQties",
                                select: ["**"],
                                page: 0,
                                size: 1000000,
                                sort: [{ name: "createDate", type: "ASC" }]
                            }
                        ]
                    },
                    {
                        name: "inspections",
                        select: ["id", "version"],
                        page: 0,
                        size: 1,
                        where: [
                            { name: "isActive", type: "Boolean", operator: "=", value: true },
                            { name: "isTemp", type: "Boolean", operator: "=", value: false },
                            { name: "refStatus.key", type: "String", operator: "!=", value: "cancel" }
                        ],
                        sort: [{ name: "createDate", type: "DESC" }],
                        children: [
                            {
                                name: "products",
                                select: [
                                    "*",
                                    "refInformProduct.**",
                                    "refInformProduct.refHazObject.refSpecies.*",
                                    "refInformProduct.refCoordinateCustoms.refUnit.*",
                                    "refInformProduct.refCoordinateCustoms.refDetailSubStanceType.*",
                                    "refInformProduct.refHaz.refManufactureCountry.*",
                                    "refInformProduct.refHazLicense.refQuantityUnit.*"
                                ],
                                page: 0,
                                size: 1000000,
                                where: [{ name: "refInformProduct.refHazSpecies.key", type: "String", operator: "!=", value: "1" }],
                                sort: [{ name: "createDate", type: "ASC" }],
                                children: [
                                    {
                                        name: "productQties",
                                        select: ["**"],
                                        page: 0,
                                        size: 1000000,
                                        sort: [{ name: "createDate", type: "ASC" }]
                                    }
                                ]
                            }
                        ]
                    }
                ]
            }
        })
        .then(page => page.data[0]);
};

export const createField = ({ props, data, key, isLoading }) => {
    const { viewOnly, menuContext, dataKey, request } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInform`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.hc.refInform"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInform,
                            "onUpdate:value": value => {
                                lodash.remove(model.products, d => !d.id);
                                model.products.forEach(d => (d.isDelete = true));
                                if (value) {
                                    callInformFull({ id: value.id }).then(inform => {
                                        model.refInform = inform;
                                        model.products = getProducts({ inform });
                                        if (model.products.length === 1) {
                                            model.products[0].isHc = true;
                                            model.tradeNameEn = model.products[0]?.refInformProduct?.refHaz?.tradeNameEn || null;
                                        }
                                    });
                                } else {
                                    model.refInform = null;
                                }
                            },
                            disabled: viewOnly || !model.refUserCompany?.id,
                            list: ({ searchQuery }) => callInform({ searchQuery, userCompanyId: model.refUserCompany.id }),
                            renderLabel: ({ item }) => <div>{item.approveCode} {item.refInspection?.refSequestrate?.key === "1" ? "(พิจารณาอายัดสินค้าแจ้งตรวจ)": ""}</div>
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.approveCode`) }
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInform.lastRequestApprove.0.lastStatusApprove.0.statusDatetime`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.hcInfo.refInformApproveDate"),
                inputProp: ({ field }) => {
                    const datetime = model.refInform?.lastRequestApprove?.[0]?.lastStatusApprove?.[0]?.statusDatetime;
                    return lodash.merge(
                        {
                            value: datetime ? moment(datetime).format("YYYY-MM-DD") : null,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: createFieldInformInfo({ props, data, key: `${prefix}refInform` }),
                    skeleton: isLoading.value,
                    gutter: 24
                })
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}manufacturerEn`,
                colProp: () => ({ xs: 24, class: "mt-4 pt-3" }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 3 }, wrapperCol: { xs: 24, md: 9 } }),
                label: () => i18n.global.t("model.hc.manufacturerEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.manufacturerEn,
                            "onUpdate:value": value => (model.manufacturerEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}addresses.0`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    if (!model.addresses?.some(d => d.isDelete !== true)) {
                        return null;
                    }
                    return (
                        <>
                            <Divider class="text-bold text-16" orientation="left">
                                {i18n.global.t("views.request.hcInfo.manufactureAddressEn")}
                            </Divider>
                            <Address
                                menuContext={menuContext}
                                viewOnly={viewOnly}
                                dataProp={data}
                                dataKey={`${prefix}addresses.0`}
                                isLoading={isLoading.value}
                                locale={[{ name: "En", suffix: true }]}
                                onUpdateSome={() => (model.isSave = true)}
                            ></Address>
                        </>
                    );
                }
            },
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}tradeNameEn`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 5 }, wrapperCol: { xs: 24, md: 19 } }),
                label: () => i18n.global.t("model.hc.tradeNameEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.tradeNameEn,
                            "onUpdate:value": value => (model.tradeNameEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}receiveName`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 5 }, wrapperCol: { xs: 24, md: 19 } }),
                label: () => i18n.global.t("model.hc.receiveName"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.receiveName,
                            "onUpdate:value": value => (model.receiveName = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}receiveAddrEn`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 5 }, wrapperCol: { xs: 24, md: 19 } }),
                label: () => i18n.global.t("model.hc.receiveAddrEn"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.receiveAddrEn,
                            "onUpdate:value": value => (model.receiveAddrEn = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}portOfDischarge`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 5 }, wrapperCol: { xs: 24, md: 19 } }),
                label: () => i18n.global.t("model.hc.portOfDischarge"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.portOfDischarge,
                            "onUpdate:value": value => (model.portOfDischarge = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}reportRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 5 }, wrapperCol: { xs: 24, md: 19 } }),
                label: () => i18n.global.t("model.hc.reportRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.reportRemark,
                            "onUpdate:value": value => (model.reportRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24, class: "mt-3" }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    if (!model.refInform) {
                        return null;
                    }
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            visibleCardTitle: viewOnly,
                            viewOnlyInspection: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}products`,
                            fetchCriteria: data.product?.fetchCriteria,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly,
                            informKey: dataKey,
                            request
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "HcProductList" } })
                    );
                    return <HcProductList {...componentProp}></HcProductList>;
                }
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}declarations`,
                            fetchCriteria: data.declaration?.fetchCriteria,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "DeclarationInlineList" } })
                    );
                    return <DeclarationInlineList {...componentProp}></DeclarationInlineList>;
                }
            }
        ]
    });
};

export const getProducts = ({ inform }) => {
    const products = inform.products.map(informProduct => {
        const productQties = informProduct.productQties.map(informProductQty => {
            return { ...informProductQty, id: null, refInformProductQty: { id: informProductQty.id } };
        });
        const inspectionProduct = inform.inspections?.[0]?.products.find(d => d.refInformProduct?.id === informProduct.id);
        if (inspectionProduct) {
            const inspectionProductQties = inspectionProduct.productQties.map(inspectionProductQty => {
                return { ...inspectionProductQty, id: null, refInspectionProductQty: { id: inspectionProductQty.id } };
            });
            informProduct.isCheck = inspectionProduct.isCheck;
            productQties.push(...inspectionProductQties);
        }
        return { isHc: false, refInformProduct: informProduct, refInspectionProduct: inspectionProduct, productQties };
    });
    setProduct({ products });
    return products;
};

export const setProduct = ({ products }) => {
    products.forEach(product => {
        product.refInformProduct.isCheck = product.refInspectionProduct?.isCheck;
        setProductQty({ productQties: product.productQties });
    });
};

export const setProductQty = ({ productQties }) => {
    productQties.forEach(productQty => {
        if (productQty.refInspectionProductQty) {
            productQty.docName = i18n.global.t("model.hc.refInspection");
        } else if (productQty.refInformProductQty) {
            productQty.docName = i18n.global.t("model.hc.refInform");
        }
    });
};
